import React, { useCallback, useEffect, useRef, useState } from "react"
import { useHistory } from "react-router-dom"
import { Prompt } from "react-router"
import CustomModal from "../components/basics/interface/CustomModal"
import { marquerSaisie } from "../utilities/versionDeploiement"

/**
 * Garde-fou de navigation (V2). Bloque le départ d'une fiche tant qu'il y a des
 * modifications non sauvegardées, et confirme via un CustomModal.
 *
 * État central : `canQuit`
 *   - true au départ (rien à sauver) → on peut quitter librement
 *   - setDirty()  → false (une modif a été faite)
 *   - setClean()  → true  (sauvegarde réussie)
 *
 * Usage dans une fiche :
 *   const { setDirty, setClean, QuitGuard } = useQuitGuard()
 *   // dans les onChange/update → setDirty()
 *   // après un save réussi      → setClean()
 *   return (<> ... {QuitGuard} </>)
 *
 * `onBeforeQuit` (optionnel) est appelé juste avant de naviguer (ex. fermerFiche).
 */
export function useQuitGuard({ onBeforeQuit } = {}) {
	const history = useHistory()
	const [canQuit, setCanQuit] = useState(true)
	// Destination en attente : non-null → le modal de confirmation est affiché.
	const [pendingLocation, setPendingLocation] = useState(null)
	// Laisse passer la navigation déclenchée par "Quitter" (synchrone, pas un state).
	const confirmedRef = useRef(false)

	// Rechargement auto au deploy : clé de cette fiche dans les saisies.
	const cleRef = useRef(Symbol("saisie"))

	// Revue 7 (Re3) : marquée TOUT DE SUITE (un masquage d'onglet juste après
	// le blur n'attend pas l'effet du rendu suivant).
	const setDirty = useCallback(() => {
		confirmedRef.current = false
		marquerSaisie(cleRef.current, true)
		setCanQuit(false)
	}, [])

	const setClean = useCallback(() => {
		confirmedRef.current = false
		marquerSaisie(cleRef.current, false)
		setCanQuit(true)
	}, [])

	// <Prompt> : intercepte la navigation SPA quand on est "dirty".
	const handleBlockedNavigation = useCallback((nextLocation) => {
		if (confirmedRef.current) return true // l'utilisateur a confirmé → on laisse
		setPendingLocation(nextLocation) // sinon on bloque et on affiche le modal
		return false
	}, [])

	const cancelQuit = useCallback(() => setPendingLocation(null), [])

	const confirmQuit = useCallback(() => {
		confirmedRef.current = true
		if (onBeforeQuit) onBeforeQuit()
		const loc = pendingLocation
		setPendingLocation(null)
		// On navigue vers la destination COMPLÈTE (pathname + search + hash + state),
		// pas juste le pathname (le bug de Quit2Modal).
		if (loc) history.push(loc)
		// Puis on DÉSARME : le blocage de <Prompt> est résolu de façon
		// synchrone pendant le push (message non-string → pas de
		// getUserConfirmation), donc à ce point la navigation est jouée.
		// Sans ça, `confirmedRef` restait armé alors que canQuit valait
		// toujours false : toute navigation suivante passait sans contrôle
		// jusqu'au prochain setDirty/setClean — et si le chargement de la
		// fiche d'arrivée échouait, ce moment n'arrivait jamais.
		// L'utilisateur a accepté de perdre sa saisie : plus rien à garder.
		confirmedRef.current = false
		setCanQuit(true)
	}, [pendingLocation, history, onBeforeQuit])

	// Rechargement auto au deploy : une fiche « dirty » le retient.
	useEffect(() => {
		const cle = cleRef.current
		marquerSaisie(cle, !canQuit)
		return () => marquerSaisie(cle, false)
	}, [canQuit])

	// Avertissement natif sur fermeture/refresh d'onglet (hors navigation SPA).
	useEffect(() => {
		const handler = (e) => {
			if (canQuit) return
			e.preventDefault()
			e.returnValue = ""
		}
		window.addEventListener("beforeunload", handler)
		return () => window.removeEventListener("beforeunload", handler)
	}, [canQuit])

	// Clavier sur le modal : Échap = annuler, Entrée = quitter.
	useEffect(() => {
		if (!pendingLocation) return
		const onKey = (e) => {
			if (e.key === "Escape") cancelQuit()
			else if (e.key === "Enter") confirmQuit()
		}
		window.addEventListener("keydown", onKey)
		return () => window.removeEventListener("keydown", onKey)
	}, [pendingLocation, cancelQuit, confirmQuit])

	const QuitGuard = (
		<>
			<Prompt when={!canQuit} message={handleBlockedNavigation} />
			{pendingLocation && (
				<CustomModal
					title="Quitter sans sauvegarder ?"
					subtitle="Les modifications non enregistrées seront perdues."
					onClose={cancelQuit}
					showCloseButton={false}
					buttons={[
						{
							title: "Annuler",
							styleName: "secondary",
							onClick: cancelQuit,
						},
						{
							title: "Quitter",
							styleName: "danger",
							onClick: confirmQuit,
						},
					]}
				/>
			)}
		</>
	)

	return { canQuit, setDirty, setClean, QuitGuard }
}

export default useQuitGuard
